import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { ProjectApi } from '../../../network';
import { ProjectAdminModel } from '../../../network/rest';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import AddEntryButton from '../../components/AddEntryButton';
import Modal from '../../components/Modal';
import useModal from '../../components/Modal/useModal';
import Title from '../../components/Title';
import WithAuthentication from '../../components/WithAuthentication';
import ProjectForm from './ProjectForm';

function AdminProjects() {
    const { isModalOpen, openModal, closeModal } = useModal();

    const [data, setData] = useState<ProjectAdminModel[]>([]);
    const [dataCount, setDataCount] = useState(0);
    const [controller, setController] = useState({
        page: 0,
        rowsPerPage: 10,
    });
    const [selectedItem, setSelectedItem] = useState<ProjectAdminModel | undefined>();

    const getData = async () => {
        try {
            const response = await ProjectApi.getProjectsAdminApiV1ProjectsAdminGet({
                offset: controller.page * controller.rowsPerPage,
                limit: controller.rowsPerPage,
            });
            if (response) {
                setData(response.projects);
                setDataCount(response.total);
            }
        } catch (e) {
            ApiErrorHandler(e);
        }
    };

    useEffect(() => {
        if (!isModalOpen) {
            getData();
        }
    }, [controller, isModalOpen]);

    async function deleteItem(id: string) {
        try {
            await ProjectApi.deleteProjectApiV1ProjectsProjectDelete({
                id,
            });

            handlePageChange(0);
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    function handlePageChange(newPage: number) {
        setController({
            ...controller,
            page: newPage,
        });
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setController({
            ...controller,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0,
        });
    };

    async function updatePublished(id: string, published: boolean) {
        try {
            if (published) {
                await ProjectApi.unpublishProjectApiV1ProjectsProjectUnpublishPost({
                    id,
                });
            } else {
                await ProjectApi.publishProjectApiV1ProjectsProjectPublishPost({
                    id,
                });
            }

            getData();
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    return (
        <WithAuthentication>
            <div className="AdminProjects">
                <Title>{translations.projects}</Title>
                <CssBaseline />
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translations.title}</TableCell>
                                <TableCell>{translations.type}</TableCell>
                                <TableCell>{translations.author}</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow
                                    key={row.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {row.title}
                                    </TableCell>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>{row.author}</TableCell>
                                    <TableCell width={170}>
                                        <Button
                                            type="button"
                                            fullWidth
                                            variant="contained"
                                            onClick={() => updatePublished(row.id, !!row.published)}
                                        >
                                            {row.published ? 'Deaktiviraj' : 'Aktiviraj'}
                                        </Button>
                                    </TableCell>
                                    <TableCell width={120}>
                                        <IconButton
                                            onClick={() => {
                                                setSelectedItem(row);
                                                openModal();
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => deleteItem(row.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        onPageChange={(e, page) => handlePageChange(page)}
                        page={controller.page}
                        count={dataCount}
                        rowsPerPage={controller.rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
                <AddEntryButton onClick={openModal} />
                <Modal
                    isOpen={isModalOpen}
                    handleClose={(e, reason) => {
                        closeModal(reason);
                        setSelectedItem(undefined);
                    }}
                    title={selectedItem ? translations.editContent : translations.insertContent}
                >
                    <ProjectForm project={selectedItem} />
                </Modal>
            </div>
        </WithAuthentication>
    );
}

export default AdminProjects;
